'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const footerLinks = {
  company: [
    { label: 'About Griott', href: '/about-griott' },
    { label: 'About Us', href: '/about' },
    { label: 'Partner With Us', href: '/partner' },
    { label: 'Careers', href: '/careers' },
    { label: 'Contact Us', href: '/contact' },
  ],
  resources: [
    { label: 'Contributor Guide', href: '/contributor-guide' },
    { label: 'Doc Projects', href: '/doc-projects' },
    { label: 'The Archive', href: '/archives' },
    { label: 'Chiefdoms', href: '/chiefdoms' },
    { label: 'Mutauro AI', href: '/mutauro' },
    { label: 'FAQ', href: '/faq' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Use', href: '/terms-of-use' },
    { label: 'Sitemap', href: '/sitemap.xml' },
  ],
};

export default function Footer() {
  const [year, setYear] = useState(2025);

  useEffect(() => {
    setYear(new Date()?.getFullYear());
  }, []);

  return (
    <footer
      className="border-t mt-auto"
      style={{ backgroundColor: 'var(--secondary)', borderColor: 'var(--border)' }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-14">
        {/* Top row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex items-center gap-2.5 mb-4 w-fit">
              <AppLogo size={28} />
              <span
                className="text-lg font-bold tracking-tight"
                style={{ color: 'var(--foreground)', fontFamily: 'var(--font-sans)' }}
              >
                Griott
              </span>
            </Link>
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
              A community-curated archive of Zimbabwean and African history — preserving oral traditions, firsthand accounts, and sourced knowledge for future generations.
            </p>
            <div className="mt-5 flex flex-col gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <span>📍 8710 Fairview, Ruwa, Zimbabwe</span>
              <a
                href="tel:+263779604727"
                className="hover:underline w-fit"
                style={{ color: 'var(--muted-foreground)' }}
              >
                📞 +263 779 604 727
              </a>
              <a
                href="mailto:hello@griott.org"
                className="hover:underline w-fit"
                style={{ color: 'var(--muted-foreground)' }}
              >
                ✉️ hello@griott.org
              </a>
            </div>
          </div>

          {/* Company */}
          <div>
            <p
              className="text-xs font-mono uppercase tracking-widest mb-4"
              style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
            >
              Company
            </p>
            <ul className="space-y-2.5">
              {footerLinks?.company?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-sm transition-colors hover:opacity-80"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <p
              className="text-xs font-mono uppercase tracking-widest mb-4"
              style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
            >
              Resources
            </p>
            <ul className="space-y-2.5">
              {footerLinks?.resources?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-sm transition-colors hover:opacity-80"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p
              className="text-xs font-mono uppercase tracking-widest mb-4"
              style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
            >
              Legal
            </p>
            <ul className="space-y-2.5">
              {footerLinks?.legal?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-sm transition-colors hover:opacity-80"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            © {year} Griott. Preserving African history, one story at a time.
          </p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Built with ❤️ in Zimbabwe 🇿🇼
          </p>
        </div>
      </div>
    </footer>
  );
}
